import React from 'react';
import { Country } from 'country-state-city';

import styles from './WorldDots.module.css';

/**
 * The corpus plotted on the world, from the live country counts.
 *
 * The dashboard's map (`dashboard/components/GlobalMap.tsx`) is Leaflet and
 * pulls a 20 MB GeoJSON from GitHub on every load — fine behind a login,
 * wrong on a public page that has to paint fast. This draws the same fact
 * with no library and no asset: every country we hold regulations for, at its
 * real latitude and longitude, sized by how much of the corpus it accounts
 * for. Nothing here is decorative — remove the data and the map is empty.
 */

/**
 * A jurisdiction code is a country when ISO 3166 says so, and a bloc when it
 * does not.
 *
 * This was a hand-kept list — EU, UN, OECD, GCC, ASEAN, APEC, AU — and "AU" is
 * Australia's ISO code, not only the African Union's initials, so Australia was
 * being struck off the map and out of the country count. There is nothing in
 * the corpus from Australia today, which is why nobody saw it. The library
 * already knows the answer, so it is asked instead of a list being maintained.
 */
export const isBloc = (code: string): boolean => !Country.getCountryByCode(code);

const W = 1000;
/* Cropped north and south of the inhabited world — the poles are a third of a
   full equirectangular frame and nothing is ever plotted there, so keeping them
   only pushes every real point into the middle. The height follows the crop so
   the scale stays square. */
const LAT_TOP = 78;
const LAT_BOTTOM = -56;
const H = Math.round((W * (LAT_TOP - LAT_BOTTOM)) / 360);

/** Equirectangular, cropped: a lat/long pair onto the drawing. */
const project = (lat: number, lon: number): [number, number] => [
  ((lon + 180) / 360) * W,
  ((LAT_TOP - lat) / (LAT_TOP - LAT_BOTTOM)) * H,
];

/** Where the equator falls in the cropped frame. */
const EQUATOR_Y = (LAT_TOP / (LAT_TOP - LAT_BOTTOM)) * H;

export interface Dot {
  code: string;
  name: string;
  count: number;
  x: number;
  y: number;
  r: number;
}

/**
 * Country codes and counts to placed, sized dots.
 *
 * The radius runs on a square root so that a country with a hundred times the
 * volume is ten times the width and not a hundred — otherwise the United
 * States is the only thing on the map.
 */
export function toDots(byCountry: Record<string, number>): Dot[] {
  const rows = Object.entries(byCountry)
    .filter(([code]) => !isBloc(code))
    .map(([code, count]) => ({ code, count, country: Country.getCountryByCode(code) }))
    .filter((r) => !!r.country);
  if (!rows.length) return [];
  const max = Math.max(...rows.map((r) => r.count), 1);
  return rows.map(({ code, count, country }) => {
    const [x, y] = project(Number(country!.latitude), Number(country!.longitude));
    return {
      code,
      name: country!.name,
      count,
      x,
      y,
      r: 4 + Math.sqrt(count / max) * 18,
    };
  }).sort((a, b) => b.r - a.r);   // big ones first, so small ones sit on top
}

interface Props {
  byCountry: Record<string, number>;
  /**
   * On a light ground the dots are a pale scatter and the graticule all but
   * disappears; the same drawing on a dark panel reads as a lit map, which is
   * what it is for. Dark is the default for that reason.
   */
  tone?: 'light' | 'dark';
  /**
   * Codes in the corpus that ISO 3166 does not know as countries, counted
   * beneath the map rather than named: today they are the European Union and
   * "XE", which means nothing to a visitor. A count is honest; a label we had
   * to guess at would not be.
   */
  blocs?: string[];
}

export const WorldDots: React.FC<Props> = ({ byCountry, blocs = [], tone = 'dark' }) => {
  const dots = React.useMemo(() => toDots(byCountry), [byCountry]);
  const [hover, setHover] = React.useState<Dot | null>(null);
  if (!dots.length) return null;

  const total = dots.reduce((n, d) => n + d.count, 0);

  return (
    <figure className={`${styles.wrap} ${tone === 'dark' ? styles.dark : ''}`}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className={styles.svg}
        role="img"
        aria-label={`${dots.length} countries covered, ${total.toLocaleString()} regulations`}
      >
        <defs>
          <radialGradient id="dotGlow">
            <stop offset="0%" stopColor="var(--ds-link)" stopOpacity="0.55" />
            <stop offset="100%" stopColor="var(--ds-link)" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* The graticule: 30° of longitude, 30° of latitude. It is what makes
            a scatter of points read as a world rather than as a chart. */}
        <g className={styles.grid}>
          {Array.from({ length: 11 }, (_, i) => (i + 1) * (W / 12)).map((x) => (
            <line key={`v${x}`} x1={x} y1={0} x2={x} y2={H} />
          ))}
          {Array.from({ length: 7 }, (_, i) => (i + 1) * (H / 8)).map((y) => (
            <line key={`h${y}`} x1={0} y1={y} x2={W} y2={y} />
          ))}
        </g>
        <line className={styles.equator} x1={0} y1={EQUATOR_Y} x2={W} y2={EQUATOR_Y} />

        {dots.map((d) => (
          <g
            key={d.code}
            onMouseEnter={() => setHover(d)}
            onMouseLeave={() => setHover(null)}
            className={styles.dotGroup}
          >
            <circle cx={d.x} cy={d.y} r={d.r * 2.2} fill="url(#dotGlow)" />
            <circle cx={d.x} cy={d.y} r={d.r} className={styles.dot} />
            <circle cx={d.x} cy={d.y} r={d.r} className={styles.ring} />
          </g>
        ))}
      </svg>

      <figcaption className={styles.caption}>
        {hover ? (
          <span className={styles.readout}>
            <strong>{hover.name}</strong> · {hover.count.toLocaleString()} regulations
          </span>
        ) : (
          <span className={styles.readout}>
            {dots.length} countries
            {blocs.length > 0 && (
              <> · {blocs.length} {blocs.length === 1 ? 'bloc' : 'blocs'}</>
            )}
            {' · '}
            <span className={styles.scale} aria-hidden="true">
              <span className={styles.scaleDot} data-size="s" />
              <span className={styles.scaleDot} data-size="m" />
              <span className={styles.scaleDot} data-size="l" />
            </span>
            {' '}fewer → more
          </span>
        )}
      </figcaption>
    </figure>
  );
};

export default WorldDots;
